import { loadPilotCatalog } from '@/modules/lab/catalog/api/catalogPilotApi'
import { isBillableReady, isPricingPending } from '@/modules/lab/catalog/api/catalogTariffApi'
import { mergeCatalogRows, type CatalogInvestigationRow } from '@/modules/lab/catalog/types'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

type CatalogPilotContextValue = {
  rows: CatalogInvestigationRow[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  patchRowLocal: (masterId: string, patch: Partial<CatalogInvestigationRow>) => void
  stats: {
    total: number
    enabled: number
    tariffMissing: number
    pricingPending: number
    billingLinked: number
    billableReady: number
    drafts: number
    localCount: number
  }
}

const CatalogPilotContext = createContext<CatalogPilotContextValue | null>(null)

export function CatalogPilotProvider({ children }: { children: ReactNode }) {
  const [rows, setRows] = useState<CatalogInvestigationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { enterprise, hospital, serverAliases } = await loadPilotCatalog()
      setRows(mergeCatalogRows(enterprise, hospital, serverAliases))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load catalog')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const patchRowLocal = useCallback((masterId: string, patch: Partial<CatalogInvestigationRow>) => {
    setRows((prev) => prev.map((r) => (r.investigationMasterId === masterId ? { ...r, ...patch } : r)))
  }, [])

  const stats = useMemo(
    () => ({
      total: rows.length,
      enabled: rows.filter((r) => r.hospitalEnabled).length,
      tariffMissing: rows.filter((r) => isPricingPending(r)).length,
      pricingPending: rows.filter((r) => isPricingPending(r)).length,
      billingLinked: rows.filter((r) => r.hospitalEnabled && r.billingLinkStatus === 'LINKED').length,
      billableReady: rows.filter((r) => isBillableReady(r)).length,
      drafts: rows.filter((r) => r.isDraft).length,
      localCount: rows.filter((r) => r.isLocal).length,
    }),
    [rows],
  )

  const value = useMemo(
    () => ({ rows, loading, error, refresh, patchRowLocal, stats }),
    [rows, loading, error, refresh, patchRowLocal, stats],
  )

  return <CatalogPilotContext.Provider value={value}>{children}</CatalogPilotContext.Provider>
}

export function useCatalogPilot(): CatalogPilotContextValue {
  const ctx = useContext(CatalogPilotContext)
  if (!ctx) throw new Error('useCatalogPilot must be used within CatalogPilotProvider')
  return ctx
}
